import { MediaType } from "../pages/Dashboard/ChatJess/types";

export type TaskStatus = "todo" | "doing" | "feedback" | "done";

export const taskDictionary: Record<TaskStatus, string> = {
  todo: "todo",
  doing: "doing",
  feedback: "feedback.",
  done: "done",
} as const;

export type HumanSupportType =
  | "requiresHumanAnswer"
  | "requiresHumanAction"
  | "requiresHumanApproval";

export interface TaskValidation {
  type: string;
  status: string;
  observation: string;
}

export interface HumanSupport {
  stepId: string;
  isRequired: boolean;
  isCompleted: boolean;
  humanSupportType?: HumanSupportType;
  humanSupportMessage?: HumanSupportMessage[];
  humanQaId?: string;
  createdAt: string;
}

export interface HumanSupportMessage {
  question: string;
  answer?: string;
  media?: MediaType;
}

export interface StepInput {
  name: string;
  value: string;
}

export interface TaskHistory {
  id: string;
  description: string;
  action: string;
  userFeedback: string;
  feedbackType: string;
  timestamp: string;
}

export interface Title {
  pt: string;
  en: string;
  es: string;
}

interface TaskBase {
  id: string;
  title: Title;
  status: TaskStatus;
  sessionId?: string;
  sessionName?: string;
  eventType?: WorkflowEventType;
  aiAgentName?: string;
  allowTakeOver?: boolean;
  takenOverByUser?: boolean;
  takenOverByHuman?: boolean;
  reviewed?: boolean;
  readyToAdvance?: boolean;
  originStatus?: TaskStatus;
  statusLabelOverride?: string;
  takeOverReason?: string;
  requiresHumanAnswer?: boolean;
  requiresHumanAction?: boolean;
  createdAt: string;
  updatedAt?: string;
  timestamp?: string | Date;
  humanQaId?: string;
  history?: TaskHistory[];
  planning: TaskPlanning;
  steps: TaskStep[];
}

// ✅ Task simples (board/lista)
export interface Task extends TaskBase {
  requiresHumanAnswer?: boolean;
  requiresHumanAction?: boolean;
  plan?: string[];
  thoughts?: string[];
  validations?: TaskValidation[];
  objective?: string;
}

// ✅ ExtendedTask (for task board with additional UI state)
export interface ExtendedTask extends Task {
  takenOverByHuman?: boolean;
  takeOverReason?: string;
}

// ✅ Task detalhada (modal)
export interface TaskDetails extends ExtendedTask {
  planning: TaskPlanning;
  thoughts: string[];
  validations: TaskValidation[];
  aiAgentName: string;
  allowTakeOver: boolean;
  statusLabel: string;
  requiresHumanAnswer: boolean;
  requiresHumanAction: boolean;
}

export type TaskDetailsMap = Record<string, TaskDetails>;

// ✅ Evento vindo do WebSocket
export interface TaskEvent {
  type: "task-created" | "task-updated" | "task-deleted";
  payload: Task;
}

export interface TaskPlanning {
  summary: string;
  text: string[];
  requiresFeedback: boolean;
  workflowCategory: string;
}

export interface TaskStep {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  isCompleted: boolean;
  humanSupport?: HumanSupport[];
  inputs: StepInput[] | string;
  output: any | string;
  substeps?: TaskSubStep[];
}

export interface TaskSubStep {
  title: string;
  updatedAt: string;
  createdAt: string;
  isCompleted: boolean;
  executionDurationInMs?: number;
  executionTime?: number;
  inputs: StepInput[] | any[];
  output: any | string;
}

export type TaskType =
  | "qa_request"
  | "user_follow_up"
  | "action_request"
  | "take_over"
  | "pending_message"
  | "platform_mode"
  | "pending_human_qa_request"
  | "user_journey"
  | "instagram_review"
  | "exceptional_day_report"
  | "status_increment";

export interface FeedbackChatMessage {
  id: string;
  text: string;
  type: HumanSupportType;
  isCurrentUser: boolean;
}

export enum WorkflowEventType {
  // Workflow Lifecycle Events
  Planned = "planned",
  Proposed = "proposed",
  Executed = "executed",
  FunctionExecuted = "functionExecuted",
  Completed = "completed",

  // Pause States
  PausedDueHumanSupportRequired = "pausedDueHumanSupportRequired",
  PausedDueApprovalRequired = "pausedDueApprovalRequired",
  PausedDueMissingInput = "pausedDueMissingInput",
  PausedDueMissingUserConfirmation = "pausedDueMissingUserConfirmation",
  Paused = "paused",
  Resumed = "resumed",

  // Dependency & State Management
  DependencyExecuted = "dependencyExecuted",
  InputFulfillDependencyExecuted = "inputFulfillDependencyExecuted",
  StatePersisted = "statePersisted",
  StateRestored = "stateRestored",
}

export const workflowEventDictionary: Record<WorkflowEventType, string> = {
  [WorkflowEventType.Planned]: "planned",
  [WorkflowEventType.Proposed]: "proposed",
  [WorkflowEventType.Executed]: "executed",
  [WorkflowEventType.FunctionExecuted]: "functionExecuted",
  [WorkflowEventType.Completed]: "completed",
  [WorkflowEventType.PausedDueHumanSupportRequired]:
    "pausedDueHumanSupportRequired",
  [WorkflowEventType.PausedDueApprovalRequired]: "pausedDueApprovalRequired",
  [WorkflowEventType.PausedDueMissingInput]: "pausedDueMissingInput",
  [WorkflowEventType.PausedDueMissingUserConfirmation]:
    "pausedDueMissingUserConfirmation",
  [WorkflowEventType.Paused]: "paused",
  [WorkflowEventType.Resumed]: "resumed",
  [WorkflowEventType.DependencyExecuted]: "dependencyExecuted",
  [WorkflowEventType.InputFulfillDependencyExecuted]:
    "inputFulfillDependencyExecuted",
  [WorkflowEventType.StatePersisted]: "statePersisted",
  [WorkflowEventType.StateRestored]: "stateRestored",
} as const;
